/**
 * Página de Campanhas - Listagem e gerenciamento de campanhas de disparo em massa
 * 
 * Design moderno com gradientes, animações e cards interativos
 * Inclui verificação de acesso ao módulo de campanhas
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { CampaignsPaywall } from '@/components/tenant/CampaignsPaywall';
import { 
  Plus, 
  Megaphone,
  RefreshCw,
  Filter,
  X,
  Clock,
  Play,
  Pause,
  XCircle,
  CheckCircle2,
  AlertCircle,
  LayoutGrid,
  List,
  Inbox,
  MessageSquare,
  Instagram,
  Send as SendIcon,
  Globe,
  MessagesSquare,
  FileSpreadsheet,
  ListChecks,

} from 'lucide-react';
// Navegação para a Central de Importação de Listas (botão "Importar Lista")
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreateCampaignDialog } from '@/components/tenant/campaigns/CreateCampaignDialog';
import { EditCampaignDialog } from '@/components/tenant/campaigns/EditCampaignDialog';
import { CampaignCard, CampaignRecipientStats } from '@/components/tenant/campaigns/CampaignCard';
import { CampaignListRow } from '@/components/tenant/campaigns/CampaignListRow';
import { CampaignQueueStats } from '@/components/tenant/campaigns/CampaignQueueStats';
import { ListPageKpis, KpiCard } from '@/components/layout/list-page';
import { Users, Send, Eye, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
// Visibilidade unificada
import ManageVisibilityDialog from '@/components/shared/ManageVisibilityDialog';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

// Tipos locais para campanhas - inclui campos opcionais para edição
interface Campaign {
  id: string;
  name: string;
  description: string | null;
  channel_type: string;
  message_content: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  // Campos opcionais usados na edição
  delay_min_ms?: number;
  delay_max_ms?: number;
  block_size?: number;
  block_delay_min_ms?: number;
  block_delay_max_ms?: number;
  use_contact_name?: boolean;
  verify_whatsapp?: boolean;
  // Segmentação dinâmica (JSONB)
  segment_data?: any;
  // Visibilidade (v1.0.0 - 2026-06-25)
  visibility?: EntityVisibility | null;
}

// Mapeamento de status para exibição
const statusConfig: Record<string, { label: string; icon: any }> = {
  draft: { label: 'Rascunho', icon: Clock },
  scheduled: { label: 'Agendada', icon: Clock },
  running: { label: 'Em execução', icon: Play },
  paused: { label: 'Pausada', icon: Pause },
  completed: { label: 'Finalizada', icon: CheckCircle2 },
  cancelled: { label: 'Cancelada', icon: XCircle },
  failed: { label: 'Falha', icon: AlertCircle },
};

// Mapeamento de tipo de canal
// Mapeamento de tipo de canal para exibição no filtro
const channelLabels: Record<string, string> = {
  whatsapp: 'WhatsApp',
  whatsapp_oficial: 'WhatsApp Oficial',
  messenger: 'Messenger',
  instagram: 'Instagram',
  telegram: 'Telegram',
  webchat: 'WebChat',
};

/**
 * Ícone do canal para uso nos filtros rápidos (padronizado com ConversationList).
 * Mantém cores de marca consistentes entre as páginas.
 */
const getChannelIcon = (channelType: string) => {
  switch (channelType) {
    case 'whatsapp':
    case 'whatsapp_oficial':
      return <MessageSquare className="h-3.5 w-3.5 text-green-600" />;
    case 'instagram':
      return <Instagram className="h-3.5 w-3.5 text-pink-500" />;
    case 'messenger':
      return <MessagesSquare className="h-3.5 w-3.5 text-blue-500" />;
    case 'telegram':
      return <SendIcon className="h-3.5 w-3.5 text-sky-500" />;
    case 'webchat':
      return <Globe className="h-3.5 w-3.5 text-muted-foreground" />;
    default:
      return <Inbox className="h-3.5 w-3.5 text-muted-foreground" />;
  }
};

export default function Campaigns() {
  const { currentTenant } = useAuth();
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const navigate = useNavigate();
  const { hasCampaignsModule, isLoading: isLoadingLimits, limits } = useResourceLimits();
  const queryClient = useQueryClient();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Quick Create (?new=1): abre o dialog "Nova campanha".
  useAutoOpenFromQuery('1', () => setCreateDialogOpen(true));

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  
  // Estados para dialog de edição
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState<Campaign | null>(null);
  // Visibilidade unificada (v1.0.0 - 2026-06-25)
  const [visibilityCampaign, setVisibilityCampaign] = useState<Campaign | null>(null);
  
  // Estados para auto-refresh inteligente
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const prevCampaignsRef = useRef<Campaign[]>();

  // Estados para filtros - padrão: draft, scheduled, running, paused
  // Filtro padrão inclui também 'completed' para exibir campanhas finalizadas ao abrir a página
  const [statusFilter, setStatusFilter] = useState<string[]>(['draft', 'scheduled', 'running', 'paused', 'completed']);
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [inboxFilter, setInboxFilter] = useState<string>('all');
  // Filtro por lista importada (origem dos destinatários) — RPC v1.3.0
  const [listFilter, setListFilter] = useState<string>('all');


  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Estado para modo de visualização com persistência
  type ViewMode = 'cards' | 'list';
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('campaigns_viewMode');
    // Padrão: lista. Cards apenas se o usuário tiver salvo explicitamente essa preferência.
    return (saved === 'cards' || saved === 'list') ? saved : 'list';
  });

  // Handler para mudar modo de visualização
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('campaigns_viewMode', mode);
  };

  // Reset página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, channelFilter, inboxFilter, listFilter]);

  // Buscar inboxes do tenant para filtro
  const { data: inboxes } = useQuery({
    queryKey: ['inboxes-filter', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, channel_type')
        .eq('tenant_id', currentTenant.id)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant?.id,
  });

  // Buscar listas importadas do tenant para o filtro "Lista" (RPC existente)
  const { data: contactLists } = useQuery({
    queryKey: ['campaign-lists-filter', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase.rpc('get_campaign_lists', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string }>;
    },
    enabled: !!currentTenant?.id,
  });



  // Buscar campanhas do tenant com filtros e auto-refresh condicional
  // Paginação 100% server-side (RPC v1.2.0): envia p_limit/p_offset e
  // lê total_count vindo na 1ª linha do retorno.
  const { data: campaignsResult, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['campaigns', currentTenant?.id, statusFilter, channelFilter, inboxFilter, listFilter, currentPage, pageSize],
    queryFn: async () => {
      if (!currentTenant?.id) return { rows: [] as Campaign[], total: 0 };

      // Listagem 100% server-side via RPC segura (padrão v2.0)
      const { data, error } = await supabase.rpc('get_tenant_campaigns_list', {
        p_status_filter: statusFilter.length > 0 ? statusFilter : null,
        p_channel_type: channelFilter !== 'all' ? channelFilter : null,
        p_inbox_id: inboxFilter !== 'all' ? inboxFilter : null,
        p_list_id: listFilter !== 'all' ? listFilter : null,

        p_tenant_hint: currentTenant.id,
        p_limit: pageSize,
        p_offset: (currentPage - 1) * pageSize,
      } as any);

      if (error) throw error;
      const rows = (data || []) as Array<Campaign & { total_count?: number | string }>;
      const total = rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0;
      return { rows: rows as Campaign[], total };
    },
    enabled: !!currentTenant?.id,
    refetchInterval: autoRefreshEnabled ? 60000 : false,
  });

  // Compatibilidade com restante do componente que espera `campaigns` array
  const campaigns = campaignsResult?.rows;
  const serverTotalCampaigns = campaignsResult?.total ?? 0;


  // KPIs agregados de todas as campanhas do tenant (RPC server-side, 1 request)
  const { data: campaignsKpis } = useQuery({
    queryKey: ['campaigns-kpis', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return null;
      const { data, error } = await supabase.rpc('get_tenant_campaigns_kpis', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      // RPC retorna 1 linha
      return (Array.isArray(data) ? data[0] : data) as {
        total_campaigns: number;
        running_campaigns: number;
        scheduled_campaigns: number;
        completed_campaigns: number;
        draft_campaigns: number;
        total_recipients: number;
        total_sent: number;
        total_delivered: number;
        total_read: number;
        total_failed: number;
        avg_delivery_rate: number | null;
        avg_read_rate: number | null;
      } | null;
    },
    enabled: !!currentTenant?.id,
    staleTime: 30_000,
    refetchInterval: autoRefreshEnabled ? 60_000 : false,
  });

  // Buscar estatísticas de recipients via RPC com agregação server-side
  // Resolve o limite de 1000 rows do PostgREST e melhora performance
  const { data: campaignStatsMap } = useQuery({
    queryKey: ['campaign-recipient-stats', campaigns?.map(c => c.id)],
    queryFn: async () => {
      if (!campaigns?.length) return {};
      
      // Chama RPC que faz COUNT + GROUP BY diretamente no banco
      const { data, error } = await supabase.rpc('get_campaigns_recipient_counts', {
        p_campaign_ids: campaigns.map(c => c.id),
        p_tenant_hint: currentTenant?.id,
      });
      
      if (error) throw error;
      
      // Mapeia array retornado para Record<campaignId, stats>
      const statsMap: Record<string, CampaignRecipientStats> = {};
      (data || []).forEach((row: any) => {
        statsMap[row.campaign_id] = {
          total: Number(row.total || 0),
          pending: Number(row.pending || 0),
          sent: Number(row.sent || 0),
          delivered: Number(row.delivered || 0),
          read: Number(row.read_count || 0),
          failed: Number(row.failed || 0),
          skipped: Number(row.skipped || 0),
        };
      });
      return statsMap;
    },
    enabled: !!campaigns?.length,
    refetchInterval: autoRefreshEnabled ? 60000 : false,
  });

  // Paginação server-side: total vem da RPC; lista já chega paginada.
  const totalCampaigns = serverTotalCampaigns;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCampaigns);

  // Dados paginados (a RPC já retorna apenas a página atual)
  const paginatedCampaigns = useMemo(() => {
    return campaigns ?? [];
  }, [campaigns]);


  // Atualiza estado de auto-refresh e timestamp quando campanhas mudam
  useEffect(() => {
    if (campaigns) {
      setLastUpdated(new Date());
      
      const hasRunning = campaigns.some(c => c.status === 'running');
      setAutoRefreshEnabled(hasRunning);
      
      if (prevCampaignsRef.current) {
        campaigns.forEach(campaign => {
          const prev = prevCampaignsRef.current?.find(c => c.id === campaign.id);
          if (prev?.status === 'running' && campaign.status === 'completed') {
            toast({
              title: 'Campanha finalizada',
              description: `"${campaign.name}" foi concluída com sucesso.`
            });
          }
        });
      }
      prevCampaignsRef.current = campaigns;
    }
  }, [campaigns]);

  // Mutation para deletar campanha
  const deleteMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: 'Campanha excluída com sucesso' });
      setDeleteDialogOpen(false);
      setSelectedCampaign(null);
    },
    onError: (error: any) => {
      toast({ 
        title: 'Erro ao excluir campanha', 
        description: error.message,
        variant: 'destructive' 
      });
    },
  });

  // Mutation para atualizar status da campanha
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: any = { status };
      
      if (status === 'running') {
        updateData.started_at = new Date().toISOString();
      } else if (status === 'paused') {
        updateData.paused_at = new Date().toISOString();
      } else if (status === 'cancelled') {
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('campaigns')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;

      // Nota: O campaign-scheduler (cron) detecta campanhas 'running' e enfileira automaticamente via PGMQ
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: 'Status atualizado com sucesso' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Erro ao atualizar status', 
        description: error.message,
        variant: 'destructive' 
      });
    },
  });

  // Mutation para duplicar campanha - delega para RPC atômica duplicate_campaign_atomic
  // que copia campos + inboxes + groups + items + actions + RECIPIENTS em uma única transação.
  const duplicateMutation = useMutation({
    mutationFn: async (campaign: Campaign) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data: newId, error } = await supabase.rpc('duplicate_campaign_atomic', {
        p_source_campaign_id: campaign.id,
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      return { id: newId as unknown as string };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: 'Campanha duplicada com sucesso' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Erro ao duplicar campanha', 
        description: error.message,
        variant: 'destructive' 
      });
    },
  });

  // Verifica se os filtros estão no padrão
  const isDefaultFilter = statusFilter.length === 5 && 
    ['draft', 'scheduled', 'running', 'paused', 'completed'].every(s => statusFilter.includes(s)) &&
    channelFilter === 'all' && 
    inboxFilter === 'all' &&
    listFilter === 'all';


  // Paywall: exibe quando tenant não tem acesso ao módulo de campanhas
  if (!isLoadingLimits && !hasCampaignsModule) {
    return <CampaignsPaywall currentPlan={limits?.plan_id} />;
  }

  // Componente de skeleton para loading
  const LoadingSkeleton = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i} className="border-t-4 border-t-muted overflow-hidden">
          <div className="p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((j) => (
                <Skeleton key={j} className="h-16 rounded-lg" />
              ))}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Campanhas</h1>
          <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Gerencie campanhas de disparos em massa</span>
            {/* Indicador de auto-refresh */}
            {autoRefreshEnabled && (
              <Badge 
                variant="outline" 
                className="text-[10px] flex items-center gap-1 bg-primary/5 border-primary/20 text-primary animate-pulse"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                </span>
                Ao vivo
              </Badge>
            )}
            {lastUpdated && (
              <span className="text-[10px] text-muted-foreground/70">
                Atualizado: {lastUpdated.toLocaleTimeString('pt-BR')}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="xs"
            onClick={async () => {
              try {
                await supabase.functions.invoke('campaign-scheduler');
              } catch (error) {
                console.error('[Campaigns] Erro ao chamar scheduler:', error);
              }
              refetch();
            }}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            <span className="hidden sm:inline ml-1.5">Atualizar</span>
          </Button>
          {/* Atalho para a Central de Importação de Listas (respeita permissão lists_contacts) */}
          {canView('lists_contacts') && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => navigate('/tenant/lists?tab=contacts')}
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              <span className="hidden sm:inline ml-1.5">Importar Lista</span>
            </Button>
          )}
          {canCreate('campaigns') && (
            <Button 
              size="xs"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="mr-1.5" />
              Nova Campanha
            </Button>
          )}
        </div>
      </div>

      {/* KPIs agregados de todas as campanhas (server-side via RPC) */}
      {campaignsKpis && (
        <ListPageKpis columns={3} className="!mt-0">
          <KpiCard
            color="purple"
            icon={Megaphone}
            label="Total Campanhas"
            value={Number(campaignsKpis.total_campaigns || 0).toLocaleString('pt-BR')}
            suffix={`${campaignsKpis.running_campaigns || 0} em execução`}
          />
          <KpiCard
            color="blue"
            icon={Clock}
            label="Agendadas"
            value={Number(campaignsKpis.scheduled_campaigns || 0).toLocaleString('pt-BR')}
          />
          <KpiCard
            color="cyan"
            icon={Users}
            label="Destinatários"
            value={Number(campaignsKpis.total_recipients || 0).toLocaleString('pt-BR')}
          />
          <KpiCard
            color="green"
            icon={Send}
            label="Enviadas"
            value={Number(campaignsKpis.total_sent || 0).toLocaleString('pt-BR')}
            suffix={`${Number(campaignsKpis.total_delivered || 0).toLocaleString('pt-BR')} entregues`}
          />
          <KpiCard
            color="orange"
            icon={Eye}
            label="Lidas"
            value={Number(campaignsKpis.total_read || 0).toLocaleString('pt-BR')}
            suffix={
              campaignsKpis.avg_read_rate != null
                ? `${Number(campaignsKpis.avg_read_rate).toFixed(1)}% leitura`
                : undefined
            }
          />
          <KpiCard
            color="red"
            icon={TrendingUp}
            label="Falhas"
            value={Number(campaignsKpis.total_failed || 0).toLocaleString('pt-BR')}
            suffix={
              campaignsKpis.avg_delivery_rate != null
                ? `${Number(campaignsKpis.avg_delivery_rate).toFixed(1)}% entrega`
                : undefined
            }
          />
        </ListPageKpis>
      )}

      {/* KPIs da Fila de Processamento (tempo real) */}
      <CampaignQueueStats />

      {/* Barra de Filtros com toggle de visualização */}
      <div className="space-y-3">
        {/* Linha 1: Filtros em grid no mobile, flex no desktop */}
        <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:items-center">
          {/* Filtro de Status */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="xs"
                className={cn(
                  'w-full sm:min-w-[140px] sm:w-auto h-8 text-xs sm:text-sm transition-all',
                  statusFilter.length > 0 && statusFilter.length < 7 && 'border-primary/50'
                )}
              >
                <Filter className="mr-1 sm:mr-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
                <span className="truncate">Status</span>
                {statusFilter.length > 0 && statusFilter.length < 7 && (
                  <Badge variant="secondary" className="ml-1 sm:ml-1.5 bg-primary/10 text-primary text-xs px-1 sm:px-1.5">
                    {statusFilter.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(statusConfig).map(([key, config]) => {
                const StatusIcon = config.icon;
                return (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={statusFilter.includes(key)}
                    onCheckedChange={(checked) => {
                      setStatusFilter(prev => 
                        checked ? [...prev, key] : prev.filter(s => s !== key)
                      );
                    }}
                  >
                    <StatusIcon className="h-4 w-4 mr-2" />
                    {config.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setStatusFilter([])}>
                Limpar seleção
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filtro de Canal - padronizado com ConversationList (ícone + cor de marca) */}
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className={cn(
              'w-full sm:w-[180px] h-8 text-xs sm:text-sm rounded-lg',
              channelFilter !== 'all' && 'border-primary/50'
            )}>
              <SelectValue placeholder="Todos os canais" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Todos os canais</span>
                </div>
              </SelectItem>
              {Object.keys(channelLabels).length > 0 && <SelectSeparator />}
              {Object.entries(channelLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    {getChannelIcon(key)}
                    <span>{label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro de Inbox - padronizado com ConversationList */}
          <Select value={inboxFilter} onValueChange={setInboxFilter}>
            <SelectTrigger className={cn(
              'w-full sm:w-[220px] h-8 text-xs sm:text-sm rounded-lg',
              inboxFilter !== 'all' && 'border-primary/50'
            )}>
              <SelectValue placeholder="Todas as Inbox" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Todas as Inbox</span>
                </div>
              </SelectItem>
              {(inboxes?.length ?? 0) > 0 && <SelectSeparator />}
              {inboxes?.map((inbox) => (
                <SelectItem key={inbox.id} value={inbox.id}>
                  <div className="flex items-center gap-2 w-full">
                    {getChannelIcon(inbox.channel_type)}
                    <span className="truncate flex-1">{inbox.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro de Lista importada (origem dos destinatários) */}
          <Select value={listFilter} onValueChange={setListFilter}>
            <SelectTrigger className={cn(
              'w-full sm:w-[220px] h-8 text-xs sm:text-sm rounded-lg',
              listFilter !== 'all' && 'border-primary/50'
            )}>
              <SelectValue placeholder="Todas as listas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <ListChecks className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Todas as listas</span>
                </div>
              </SelectItem>
              {(contactLists?.length ?? 0) > 0 && <SelectSeparator />}
              {contactLists?.map((list) => (
                <SelectItem key={list.id} value={list.id}>
                  <div className="flex items-center gap-2 w-full">
                    <ListChecks className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate flex-1">{list.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>


        {/* Linha 2: Botão restaurar + Toggle de visualização */}
        <div className="flex items-center justify-between">
          {/* Botão para restaurar filtros padrão */}
          {!isDefaultFilter ? (
            <Button 
              variant="ghost" 
              size="xs"
              onClick={() => {
                setStatusFilter(['draft', 'scheduled', 'running', 'paused']);
                setChannelFilter('all');
                setInboxFilter('all');
                setListFilter('all');

              }}
              className="text-muted-foreground hover:text-foreground text-xs sm:text-sm"
            >
              <X className="mr-1 h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <span className="hidden sm:inline">Restaurar padrão</span>
              <span className="sm:hidden">Limpar</span>
            </Button>
          ) : (
            <div /> // Placeholder para manter o toggle à direita
          )}

          {/* Toggle de visualização */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1 border border-border">
            <button
              onClick={() => handleViewModeChange('cards')}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                viewMode === 'cards'
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                viewMode === 'list'
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Lista de campanhas */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : campaigns && campaigns.length > 0 ? (
        <div className="space-y-6">
          {/* Renderização condicional: Cards ou Lista */}
          {viewMode === 'cards' ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {paginatedCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  recipientStats={campaignStatsMap?.[campaign.id]}
                  canEdit={canEdit('campaigns')}
                  canCreate={canCreate('campaigns')}
                  canDelete={canDelete('campaigns')}
                  onStart={() => updateStatusMutation.mutate({ id: campaign.id, status: 'running' })}
                  onPause={() => updateStatusMutation.mutate({ id: campaign.id, status: 'paused' })}
                  onResume={() => updateStatusMutation.mutate({ id: campaign.id, status: 'running' })}
                  onCancel={() => updateStatusMutation.mutate({ id: campaign.id, status: 'cancelled' })}
                  onDuplicate={() => duplicateMutation.mutate(campaign)}
                  onDelete={() => {
                    setSelectedCampaign(campaign);
                    setDeleteDialogOpen(true);
                  }}
                  onEdit={() => {
                    setCampaignToEdit(campaign);
                    setEditDialogOpen(true);
                  }}
                  onManageVisibility={() => setVisibilityCampaign(campaign)}
                />

              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {paginatedCampaigns.map((campaign) => (
                    <CampaignListRow
                      key={campaign.id}
                      campaign={campaign}
                      recipientStats={campaignStatsMap?.[campaign.id]}
                      canEdit={canEdit('campaigns')}
                      canCreate={canCreate('campaigns')}
                      canDelete={canDelete('campaigns')}
                      onStart={() => updateStatusMutation.mutate({ id: campaign.id, status: 'running' })}
                      onPause={() => updateStatusMutation.mutate({ id: campaign.id, status: 'paused' })}
                      onResume={() => updateStatusMutation.mutate({ id: campaign.id, status: 'running' })}
                      onCancel={() => updateStatusMutation.mutate({ id: campaign.id, status: 'cancelled' })}
                      onDuplicate={() => duplicateMutation.mutate(campaign)}
                      onDelete={() => {
                        setSelectedCampaign(campaign);
                        setDeleteDialogOpen(true);
                      }}
                      onEdit={() => {
                        setCampaignToEdit(campaign);
                        setEditDialogOpen(true);
                      }}
                      onManageVisibility={() => setVisibilityCampaign(campaign)}
                    />

                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer de paginação */}
          <PaginationFooter
            totalItems={totalCampaigns}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[6, 9, 12, 18]}
            borderClassName="border-t"
          />
        </div>
      ) : (
        /* Empty state moderno */
        <Card className="py-16 border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
              <div className="relative rounded-full bg-gradient-to-br from-primary/10 to-primary/5 p-6">
                <Megaphone className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-2">Nenhuma campanha encontrada</h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              {!isDefaultFilter 
                ? 'Tente ajustar os filtros ou crie uma nova campanha.'
                : 'Crie sua primeira campanha para enviar mensagens em massa para seus contatos.'
              }
            </p>
            <div className="flex gap-3">
              {!isDefaultFilter && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setStatusFilter(['draft', 'scheduled', 'running', 'paused']);
                    setChannelFilter('all');
                    setInboxFilter('all');
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar Filtros
                </Button>
              )}
              {canCreate('campaigns') && (
                <Button 
                  onClick={() => setCreateDialogOpen(true)}
                  className="transition-all hover:shadow-lg hover:scale-[1.02]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Campanha
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog de criação */}
      <CreateCampaignDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen}
        tenantId={currentTenant?.id || ''}
      />

      {/* Dialog de edição - controlado por open, campaign pode ser null durante fechamento */}
      <EditCampaignDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            // Delay para permitir animação de fechamento do dialog
            setTimeout(() => setCampaignToEdit(null), 150);
          }
        }}
        campaign={campaignToEdit}
      />

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir campanha?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A campanha "{selectedCampaign?.name}" e todos os seus destinatários serão permanentemente removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedCampaign && deleteMutation.mutate(selectedCampaign.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de visibilidade unificado (Listas/Campanhas/Templates) */}
      <ManageVisibilityDialog
        open={!!visibilityCampaign}
        onOpenChange={(open) => !open && setVisibilityCampaign(null)}
        entityType="campaign"
        entityId={visibilityCampaign?.id}
        entityLabel={visibilityCampaign?.name}
      />
    </div>
  );
}
